import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '../assets/close.svg';
import { useTranslation } from 'react-i18next';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

const LoginModal = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [show, setShow] = useState(false);
  const [emailError, setEmailError] = useState('');
  const emailInputRef = useRef(null);
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setShow(true);
        emailInputRef.current?.focus();
      }, 10);
    } else {
      setShow(false);
    }
  }, [isOpen]);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setEmailError(t('logininvalidemail'));
      return;
    } else {
      setEmailError('');
    }

    if (email) {
      onSubmit(email);
    }
  };

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className={`fixed top-0 left-0 w-full h-dvh bg-[#212121] bg-opacity-[0.46] z-[60] transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`fixed w-full lg:w-[550px] bottom-0 lg:top-0 lg:right-0 h-auto max-h-[85dvh] lg:max-h-none lg:h-full bg-white z-[61] transform transition-transform duration-300 ease-in-out p-4 overflow-y-auto rounded-t-2xl lg:rounded-none ${
          show ? 'translate-y-0 lg:translate-x-0' : 'translate-y-full lg:translate-x-full lg:translate-y-0'
        }`}
      >
        {/* Close Icon */}
        <div className='flex justify-end w-full lg:pb-4'>
          <button
            className='w-9 h-9 flex items-center justify-center rounded-full transition-colors duration-300 hover:bg-[#e7e6e6bf]'
            onClick={onClose}
          >
            <img src={CloseIcon} alt="CloseIcon" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <h1 className='text-4xl font-semibold lg:mt-3 mb-1 capitalize'>
            {t('welcome')}
          </h1>

          <p className='text-[#918d8d] text-sm'>
            {t('logintext')}
          </p>

          <p className='text-base font-semibold mt-4 lg:mt-6'>
            {t('loginsignup')}
          </p>

          <input
            type="email"
            ref={emailInputRef}
            placeholder={t('logininputplaceholder')}
            className='mt-4 lg:mt-6 rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {emailError && (
            <p className="text-[#ef4444] text-sm mt-1">{emailError}</p>
          )}

          <button
            type="submit"
            className='h-[52px] px-5 text-center text-base capitalize rounded-lg mt-8 lg:mt-12 w-full font-medium transition-colors duration-300'
            style={{
              backgroundColor: email ? '#0b1223' : 'rgba(0,0,0,.12)',
              color: email ? '#ffffff' : 'rgba(0,0,0,.26)',
            }}
          >
            {t('logincontinuebtn')}
          </button>
        </form>
      </div>
    </>,
    document.body,
  );
};

export default LoginModal;
